import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { ApiClient } from './api.client.js';

// ── Types (mirror @okvc/schema; kept local so the web-app doesn't pull a
// server-side lib through Vite's bundle).

export interface PreferenceRow {
  key: string;
  value: unknown;
  updatedAt: string;
}

export interface PreferencesListResponse {
  items: PreferenceRow[];
}

export interface UpsertPreferenceBody {
  value: unknown;
}

export const MeApiKeys = {
  Preferences: 'MePreferences',
};

export class MeApi extends ApiClient {
  public static getPreferences(): Promise<PreferencesListResponse> {
    return this.http.get<PreferencesListResponse>('/me/preferences').then((res) => res.data);
  }

  public static upsertPreference(key: string, value: unknown): Promise<PreferenceRow> {
    return this.http
      .put<PreferenceRow>(`/me/preferences/${encodeURIComponent(key)}`, { value })
      .then((res) => res.data);
  }
}

/**
 * Boot-time fetch of all current-user preferences. Reads off the `MePreferences`
 * cache for the rest of the session — `useUserPreference` reads from this
 * cache and optimistically updates it on writes.
 */
export function useAllPreferences(
  queryOptions?: UseQueryOptions<PreferencesListResponse>,
) {
  return useQuery<PreferencesListResponse>(
    [MeApiKeys.Preferences],
    () => MeApi.getPreferences(),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );
}

/**
 * Internal-use mutation. Most callers should use the `useUserPreference` hook
 * (`hooks/use-user-preference.ts`) which adds debouncing + optimistic cache
 * patching on top of this.
 */
export function useUpsertPreferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ key, value }: { key: string; value: unknown }) => MeApi.upsertPreference(key, value),
    {
      onSuccess: (row) => {
        queryClient.setQueryData<PreferencesListResponse | undefined>(
          [MeApiKeys.Preferences],
          (curr) => {
            if (!curr) return { items: [row] };
            const others = curr.items.filter((i) => i.key !== row.key);
            return { items: [row, ...others] };
          },
        );
      },
    },
  );
}
